import {
  MdAccessTime,
  MdAssignmentInd,
  MdAutorenew,
  MdCheckCircle,
  MdDelete,
  MdDoNotDisturbOn,
  MdPerson,
} from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge/Badge";
import ActionMenu from "components/heartbeat/ActionMenu";
import { formatDate, formatRelative } from "utils/format";
import {
  getActionItemName,
  getPriorityMeta,
  getStatusMeta,
  getTypeIcon,
  getTypeLabel,
} from "./actionItemMeta";

const ActionItemRow = ({ item, onViewProfile, onAssign, onStatusChange, onDelete }) => {
  const name = getActionItemName(item);
  const priority = getPriorityMeta(item.priority);
  const status = getStatusMeta(item.status);
  const TypeIcon = getTypeIcon(item.actionType);
  const isUnresolved = item.status !== "completed" && item.status !== "dismissed";
  const contacts = [item.response?.followUpEmail, item.response?.followUpPhone].filter(Boolean);

  const menuItems = [
    item.userId && {
      id: "profile",
      label: "View Guest Profile",
      icon: MdPerson,
      onSelect: onViewProfile,
    },
    { id: "assign", label: "Assign to Staff", icon: MdAssignmentInd, onSelect: onAssign },
    {
      id: "in-progress",
      label: "Mark In Progress",
      icon: MdAutorenew,
      onSelect: () => onStatusChange("in_progress"),
    },
    {
      id: "completed",
      label: "Mark Completed",
      icon: MdCheckCircle,
      onSelect: () => onStatusChange("completed"),
    },
    {
      id: "dismiss",
      label: "Dismiss",
      icon: MdDoNotDisturbOn,
      onSelect: () => onStatusChange("dismissed"),
    },
    { id: "delete", label: "Delete", icon: MdDelete, tone: "danger", onSelect: onDelete },
  ].filter(Boolean);

  return (
    <Card padding="sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
            <TypeIcon aria-hidden="true" className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-fg">{name}</h2>
              <Badge tone={priority.tone}>{priority.label}</Badge>
              <Badge tone={status.tone}>{status.label}</Badge>
            </div>
            <p className="mt-0.5 text-xs text-fg-muted">Created {formatDate(item.createdAt)}</p>
            <p className="mt-0.5 text-sm text-fg-muted">
              {[getTypeLabel(item.actionType), ...contacts].join(" • ")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <p className="flex items-center gap-2 text-sm text-fg-muted">
            <MdPerson aria-hidden="true" className="h-5 w-5" />
            {item.assignee
              ? `${item.assignee.firstName} ${item.assignee.lastName}`
              : "Unassigned"}
          </p>

          {isUnresolved && (
            <p
              className="flex items-center gap-2 text-sm text-fg-muted"
              title={`Created ${formatDate(item.createdAt)}`}
            >
              <MdAccessTime aria-hidden="true" className="h-5 w-5" />
              {formatRelative(item.createdAt)}
            </p>
          )}

          <ActionMenu label={`Actions for ${name}`} items={menuItems} />
        </div>
      </div>

      {item.description && <p className="mt-3 text-sm text-fg-muted">{item.description}</p>}

      {item.notes && (
        <div className="mt-3 rounded-control bg-surface-subtle p-3 text-sm">
          <p className="font-medium text-fg">Notes</p>
          <p className="text-fg-muted">{item.notes}</p>
        </div>
      )}
    </Card>
  );
};

export default ActionItemRow;
